import { Injectable } from "@angular/core";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import GeoJSON from 'ol/format/GeoJSON';
import { Stroke, Style, Icon, Fill } from 'ol/style';
import { HttpClient } from "@angular/common/http";
import { GnssPoint, TrackData } from "../models/trip";
import { Feature } from "ol";
import { Point } from "ol/geom";
import CircleStyle from "ol/style/Circle";
import { fromLonLat } from "ol/proj";

@Injectable({
  providedIn: 'root'
})
export class WinterMapService {

  private overpassUrl = 'https://overpass-api.de/api/interpreter';

  constructor(private http: HttpClient) { }


  // -------------------------------------------------------------
  // Crea un TileLayer OSM con winter mode attivo.
  // -------------------------------------------------------------
  createWinterTileLayer(): TileLayer<OSM> {
    const layer = new TileLayer({
      source: new OSM()
    });

    layer.on('postrender', (event: any) => {
      const ctx = event.context;
      if (!ctx) return; // se WebGL, niente pixel editing

      const canvas = ctx.canvas;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      this.applyWinterMode(imageData);

      ctx.putImageData(imageData, 0, 0);
    });

    return layer;
  }

  // -------------------------------------------------------------
  // Converte il centro in un box entro cui cercare le features
  // -------------------------------------------------------------
  getBBoxFromBoundaries(trackData: TrackData): string {
    const latDegreeKm = 111.32;
    const lonDegreeKm = 111.32 * Math.cos((trackData.minLatitude + trackData.maxLatitude) * Math.PI / (2 * 180));

    const radiusKm = 1.0;
    const latDelta = radiusKm / latDegreeKm;
    const lonDelta = radiusKm / lonDegreeKm;

    const south = trackData.minLatitude - latDelta;
    const north = trackData.maxLatitude + latDelta;
    const west = trackData.minLongitude - lonDelta;
    const east = trackData.maxLongitude + lonDelta;

    return `${south},${west},${north},${east}`;
  }

  getBBoxFromCenter(lat: number, lon: number, radiusKm: number): string {
    const latDegreeKm = 111.32;
    const lonDegreeKm = 111.32 * Math.cos(lat * Math.PI / 180);

    const latDelta = radiusKm / latDegreeKm;
    const lonDelta = radiusKm / lonDegreeKm;

    const south = lat - latDelta;
    const north = lat + latDelta;
    const west = lon - lonDelta;
    const east = lon + lonDelta;

    return `${south},${west},${north},${east}`;
  }

  // -------------------------------------------------------------
  //  SKI TRACKS (Overpass API → GeoJSON → VectorLayer)
  // -------------------------------------------------------------
  loadSkiTracks(bbox: string) {
    const query = `
      [out:json][timeout:25];
      (
        way["piste:type"](${bbox});
        relation["piste:type"](${bbox});
      );
      out geom;
    `;

    return this.http.post(this.overpassUrl, query, { responseType: 'json' });
  }

  createSkiTracksLayer(overpassData: any): VectorLayer<VectorSource> {
    const geojson = this.overpassToGeoJSON(overpassData);

    const features = new GeoJSON().readFeatures(geojson, {
      featureProjection: 'EPSG:3857'
    });
    return new VectorLayer({
      source: new VectorSource({ features }),
      zIndex: 0,
      style: (feature) => {
        const difficulty = feature.get('piste:difficulty');
        let color = '#fffe91';

        if (difficulty === 'easy') color = '#00c853';
        else if (difficulty === 'intermediate') color = '#ffab00';
        else if (difficulty === 'advanced') color = '#d50000';

        return new Style({
          stroke: new Stroke({
            color,
            width: 3
          })
        });
      }
    });
  }

  createUserTrackLayer(track: GnssPoint[]): VectorLayer<VectorSource> {
    const geojson = this.trackToGeoJSON(track);

    const features = new GeoJSON().readFeatures(geojson, {
      featureProjection: 'EPSG:3857'
    });

    return new VectorLayer({
      source: new VectorSource({ features }),
      zIndex: 1,
      style: (feature) => {
        let color = '#0077ff';
        return new Style({
          stroke: new Stroke({
            color,
            width: 3
          })
        });
      }
    });
  }

  createAthleteLayer(start: GnssPoint): VectorLayer<VectorSource> {
    const geoMarker = new Feature({
      type: 'geoMarker',
      geometry: new Point(fromLonLat([start.longitude, start.latitude]))
    });

    return new VectorLayer({
      source: new VectorSource({ features: [geoMarker] }),
      zIndex: 2,
      style: (feature) => {
        let color = '#0077ff';
        return new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: 'black' }),
            stroke: new Stroke({
              color: 'white',
              width: 2,
            }),
          })
        });
      }
    });
  }

  // -------------------------------------------------------------
  //  POI (icone)
  // -------------------------------------------------------------
  loadPoi(bbox: string) {
    const query = `
    [out:json][timeout:25];
    (
      node["aerialway"="station"](${bbox});
    );
    out geom;
  `;

    return this.http.post(this.overpassUrl, query, { responseType: 'json' });
  }

  createPoiLayerFromOverpass(overpassData: any, iconUrl: string): VectorLayer<VectorSource> {
    const geojson = this.overpassToGeoJSON(overpassData);

    const features = new GeoJSON().readFeatures(geojson, {
      featureProjection: 'EPSG:3857'
    });

    return new VectorLayer({
      source: new VectorSource({ features }),
      style: new Style({
        image: new Icon({
          src: iconUrl,
          scale: 0.8
        })
      })
    });
  }

  // -------------------------------------------------------------
  // Applica il filtro "winter mode" ai pixel della tile.
  // -------------------------------------------------------------
  private applyWinterMode(imageData: ImageData): ImageData {
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];

      // Desaturazione leggera
      const avg = (r + g + b) / 3;
      r = r * 0.4 + avg * 0.6;
      g = g * 0.4 + avg * 0.6;
      b = b * 0.4 + avg * 0.6;

      // Vegetazione → blu ghiaccio
      const isGreenish = g > r + 10 && g > b + 10;
      if (isGreenish) {
        r = 185;
        g = 215;
        b = 245;
      }

      // Terreni marroni → grigio freddo
      const isBrownish = r > 80 && g > 40 && b < 80 && r > g && g > b;
      if (isBrownish) {
        r = 180;
        g = 190;
        b = 205;
      }

      // Viraggio freddo globale
      r *= 0.95;
      g *= 0.98;
      b *= 1.05;

      // Schiarimento neve
      const brighten = 1.08;
      r *= brighten;
      g *= brighten;
      b *= brighten;

      data[i] = Math.min(255, r);
      data[i + 1] = Math.min(255, g);
      data[i + 2] = Math.min(255, b);
    }

    return imageData;
  }

  // -------------------------------------------------------------
  // Converte i file overpass to Json
  // -------------------------------------------------------------
  private overpassToGeoJSON(overpass: any): any {
    const features = [];

    for (const el of overpass.elements) {
      if (!el.geometry) continue;

      const coords = el.geometry.map((g: any) => [g.lon, g.lat]);

      let geometry = null;

      if (el.type === 'way') {
        geometry = {
          type: 'LineString',
          coordinates: coords
        };
      }

      if (el.type === 'relation' && el.members) {
        const multi = el.members
          .filter((m: any) => m.geometry)
          .map((m: any) => ({
            type: 'LineString',
            coordinates: m.geometry.map((g: any) => [g.lon, g.lat])
          }));

        geometry = {
          type: 'MultiLineString',
          coordinates: multi.map((l: any) => l.coordinates)
        };
      }

      if (!geometry) continue;

      features.push({
        type: 'Feature',
        geometry,
        properties: el.tags || {}
      });
    }

    return {
      type: 'FeatureCollection',
      features
    };
  }

  private trackToGeoJSON(track: GnssPoint[]): any {
    const features = [];

    const coords = track.map((p: any) => [p.longitude, p.latitude]);
    let geometry = {
        type: 'LineString',
        coordinates: coords
      };

    features.push({
      type: 'Feature',
      geometry,
      properties: { name: "User Track" }
    });

    return {
      type: 'FeatureCollection',
      features
    };
  }
}
